import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Bell, AlertTriangle, Check, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAlerts } from '../context/AlertContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const { user, canEdit } = useAuth();
  const { fetchAlerts: refreshContextAlerts } = useAlerts();

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const acknowledged = filterStatus === 'acknowledged' ? true : filterStatus === 'pending' ? false : undefined;
      const params = acknowledged !== undefined ? `?acknowledged=${acknowledged}` : '';
      const [alertsRes, usersRes] = await Promise.all([
        axios.get(`${API}/alerts${params}`),
        canEdit() ? axios.get(`${API}/users`) : Promise.resolve({ data: [] })
      ]);
      setAlerts(alertsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/acknowledge`, {
        user_id: user.id,
        user_name: user.name
      });
      toast.success('Alert acknowledged');
      fetchData();
      refreshContextAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId || !selectedAlert) return;
    const assignedUser = users.find(u => u.id === selectedUserId);
    try {
      await axios.post(`${API}/alerts/${selectedAlert.id}/assign`, {
        user_id: selectedUserId,
        user_name: assignedUser?.name || ''
      });
      toast.success('Alert assigned');
      setAssignDialogOpen(false);
      setSelectedAlert(null);
      setSelectedUserId('');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign alert');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Low Stock Alerts</h1>
          <p className="text-slate-500 mt-1">Manage inventory alerts</p>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="alert-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Alerts</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {alerts.length > 0 ? (
        <div className="grid gap-4">
          {alerts.map((alert) => (
            <Card
              key={alert.id}
              className={`border-2 shadow-sm rounded-sm transition-all ${
                alert.acknowledged
                  ? 'border-slate-200 bg-slate-50'
                  : 'border-red-500 bg-red-50'
              }`}
              data-testid={`alert-card-${alert.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      alert.acknowledged ? 'bg-slate-200' : 'bg-red-100'
                    }`}>
                      <AlertTriangle className={`w-6 h-6 ${
                        alert.acknowledged ? 'text-slate-500' : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-lg text-slate-900">
                          {alert.part_reference}
                        </span>
                        {alert.acknowledged ? (
                          <Badge className="rounded-sm bg-emerald-100 text-emerald-700">
                            <Check className="w-3 h-3 mr-1" />
                            Acknowledged
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="rounded-sm pulse-alert">
                            Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-600 mb-2">{alert.part_description}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-red-600 font-semibold">
                          Current: {alert.current_quantity}
                        </span>
                        <span className="text-slate-500">
                          Minimum: {alert.minimum_quantity}
                        </span>
                      </div>
                      {alert.assigned_user_name && (
                        <p className="text-sm text-amber-700 mt-2">
                          Assigned to: <span className="font-medium">{alert.assigned_user_name}</span>
                        </p>
                      )}
                      {alert.acknowledged && (
                        <p className="text-sm text-slate-500 mt-2">
                          Acknowledged by {alert.acknowledged_by} on {formatDate(alert.acknowledged_at)}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        Created: {formatDate(alert.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  {!alert.acknowledged && (
                    <div className="flex gap-2 flex-shrink-0">
                      {canEdit() && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm"
                          onClick={() => {
                            setSelectedAlert(alert);
                            setAssignDialogOpen(true);
                          }}
                          data-testid={`assign-alert-btn-${alert.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Assign
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                        onClick={() => handleAcknowledge(alert.id)}
                        data-testid={`acknowledge-alert-btn-${alert.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Acknowledge
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Bell className="w-12 h-12 mb-4" />
            <p className="text-lg font-medium">No alerts</p>
            <p className="text-sm">
              {filterStatus === 'pending'
                ? 'All stock levels are healthy'
                : filterStatus === 'acknowledged'
                ? 'No acknowledged alerts'
                : 'No alerts to display'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-alert-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">Assign Alert</DialogTitle>
            <DialogDescription>
              Assign this low stock alert to a team member
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="rounded-sm" data-testid="assign-user-select">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedUserId}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="confirm-assign-btn"
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Alerts;
